import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Space,
  Row,
  Col,
  Divider,
  InputNumber,
  Modal,
  Spin,
  Alert,
  Tag,
} from "antd";
import { toast } from "react-toastify";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  QuestionCircleOutlined,
  EyeOutlined
} from "@ant-design/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getAssessmentById, updateAssessment, createAssessment } from "../Services/CourseListService";
import useCourseDetails from "../Hooks/Courses/useCourseDetails"; // Added this import

const { Title, Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

const EditAssessmentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId, moduleId } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [assessmentData, setAssessmentData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [validationAlert, setValidationAlert] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const assessmentId = queryParams.get("assessmentId");
  const isPreviewMode = queryParams.get("mode") === "preview";
  const isCreateMode = !assessmentId;

  // Added course details hook
  const { course, loading: courseLoading } = useCourseDetails(courseId);
  const isCoursePublished = course && course.isPublished;

  useEffect(() => {
    if (assessmentId) {
      loadAssessmentData();
    } else {
      setPageLoading(false);
      setQuestions([
        {
          key: `question-${Date.now()}`,
          questionText: "",
          order: 1,
          options: [
            { key: `option-${Date.now()}-1`, text: "", isCorrect: "false" },
            { key: `option-${Date.now()}-2`, text: "", isCorrect: "false" },
            { key: `option-${Date.now()}-3`, text: "", isCorrect: "false" },
          ]
        }
      ]);
    }
  }, [assessmentId]);

  // Added effect to switch to preview mode if course is published
  useEffect(() => {
    if (course && course.isPublished && !isPreviewMode && !isCreateMode) {
      switchToPreviewMode();
    }
  }, [course, isPreviewMode, isCreateMode]);

  const loadAssessmentData = async () => {
    try {
      setPageLoading(true);
      console.log(" Loading assessment data for ID:", assessmentId);

      const data = await getAssessmentById(assessmentId);
      setAssessmentData(data);

      form.setFieldsValue({
        title: data.title,
        estimatedTime: data.estimatedTime
      });

      // Convert boolean isCorrect to string "true"/"false" to match AddModulePage pattern
      const formattedQuestions = data.questions.map((question, index) => ({
        key: question.id || `question-${index}`,
        questionText: question.questionText,
        order: question.order || (index + 1),
        options: question.options.map(option => ({
          key: option.id || `option-${Math.random()}`,
          text: option.text,
          isCorrect: option.isCorrect ? "true" : "false" // Convert to string
        }))
      }));

      setQuestions(formattedQuestions);

    } catch (error) {
      console.error(" Failed to load assessment data:", error);
      toast.error("Failed to load assessment data");
      navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}`);
    } finally {
      setPageLoading(false);
    }
  };

  const addQuestion = () => {
    if (isPreviewMode || isCoursePublished) return;

    const newQuestion = {
      key: `question-${Date.now()}`,
      questionText: "",
      order: questions.length + 1,
      options: [
        { key: `option-${Date.now()}-1`, text: "", isCorrect: "false" },
        { key: `option-${Date.now()}-2`, text: "", isCorrect: "false" },
        { key: `option-${Date.now()}-3`, text: "", isCorrect: "false" },
      ]
    };
    setQuestions([...questions, newQuestion]);
  };

  const removeQuestion = (index) => {
    if (isPreviewMode || isCoursePublished) return;

    confirm({
      title: 'Delete Question?',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure you want to delete this question? This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        const newQuestions = questions.filter((_, i) => i !== index);
        const reorderedQuestions = newQuestions.map((q, i) => ({
          ...q,
          order: i + 1
        }));
        setQuestions(reorderedQuestions);
        toast.success("Question deleted successfully");
      }
    });
  };

  const updateQuestion = (index, field, value) => {
    if (isPreviewMode || isCoursePublished) return;

    const newQuestions = [...questions];
    newQuestions[index] = {
      ...newQuestions[index],
      [field]: value
    };
    setQuestions(newQuestions);
  };

  const updateOption = (questionIndex, optionIndex, field, value) => {
    if (isPreviewMode || isCoursePublished) return;

    const newQuestions = [...questions];
    newQuestions[questionIndex].options[optionIndex] = {
      ...newQuestions[questionIndex].options[optionIndex],
      [field]: value
    };
    setQuestions(newQuestions);
  };

  // Add option to a question - SAME AS AddModulePage
  const addOption = (questionIndex) => {
    if (isPreviewMode || isCoursePublished) return;

    const newQuestions = [...questions];
    if (newQuestions[questionIndex].options.length < 5) {
      newQuestions[questionIndex].options.push({
        key: `option-${Date.now()}`,
        text: "",
        isCorrect: "false"
      });
      setQuestions(newQuestions);
    } else {
      toast.warning("Maximum 5 options allowed per question");
    }
  };

  // Remove option from a question - SAME AS AddModulePage
  const removeOption = (questionIndex, optionIndex) => {
    if (isPreviewMode || isCoursePublished) return;

    const newQuestions = [...questions];
    if (newQuestions[questionIndex].options.length > 3) {
      newQuestions[questionIndex].options.splice(optionIndex, 1);
      setQuestions(newQuestions);
    } else {
      toast.warning("Each question must have at least 3 options");
    }
  };

  // Set correct answer - SAME AS AddModulePage
  const handleCorrectAnswerChange = (qIndex, optIndex) => {
    if (isPreviewMode || isCoursePublished) return;

    setQuestions(prev => {
      const updated = [...prev];
      const options = updated[qIndex].options;

      // If user tries to disable the ONLY correct answer → block
      const correctCount = options.filter(o => o.isCorrect === "true").length;
      const isCurrentlyCorrect = options[optIndex].isCorrect === "true";

      if (isCurrentlyCorrect && correctCount === 1) {
        toast.warning("At least one correct answer is required");
        return prev; // DO NOT UPDATE
      }

      // Toggle
      options[optIndex].isCorrect = isCurrentlyCorrect ? "false" : "true";

      return updated;
    });
  };

  // Validation function - SAME AS AddModulePage
  const validateQuestions = () => {
    console.log(" validateQuestions called");
    console.log("Current questions state:", JSON.parse(JSON.stringify(questions)));

    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      console.log(`Checking question ${i + 1}:`, q);

      // Must have at least 2 options
      if (!q.options || q.options.length < 2) {
        const msg = `Question ${i + 1}: must have at least 2 options`;
        console.error(` Validation failed: ${msg}`);
        setValidationAlert(msg);
        return false;
      }

      // Must have question text
      if (!q.questionText || !q.questionText.trim()) {
        const msg = `Question ${i + 1}: question text is required`;
        console.error(` Validation failed: ${msg}`);
        setValidationAlert(msg);
        return false;
      }

      // All options must have text
      const emptyOption = q.options.find((opt) => !opt.text || !opt.text.trim());
      if (emptyOption) {
        const msg = `Question ${i + 1}: all options must have text`;
        console.error(` Validation failed: ${msg}`);
        setValidationAlert(msg);
        return false;
      }

      // Must select at least one correct answer (we use "true" string in your state)
      const hasCorrect = q.options.some(opt => opt.isCorrect === "true" || opt.isCorrect === true);
      console.log(`Question ${i + 1} has correct answer:`, hasCorrect);
      if (!hasCorrect) {
        const msg = `Question ${i + 1}: please select at least one correct answer`;
        console.error(` Validation failed: ${msg}`);
        setValidationAlert(msg);
        return false;
      }

      // Max 2 correct answers
      const correctCount = q.options.filter(opt => opt.isCorrect === "true" || opt.isCorrect === true).length;
      if (correctCount > 2) {
        const msg = `Question ${i + 1}: you can select at most 2 correct answers`;
        console.error(` Validation failed: ${msg}`);
        setValidationAlert(msg);
        return false;
      }
    }

    // clear alert if OK
    setValidationAlert(null);
    console.log(" All questions passed validation");
    return true;
  };

  const handleAssessmentSubmit = async (values) => {
    if (isPreviewMode || isCoursePublished) return;

    setLoading(true);
    try {
      await form.validateFields();
      const validationPassed = validateQuestions();
      if (!validationPassed) {
        toast.error("Please fix all validation errors before submitting");
        setLoading(false);
        return;
      }

      const submitData = {
        moduleId: parseInt(moduleId),
        title: values.title?.trim() || "",
        questions: questions.map((question, index) => ({
          questionText: question.questionText?.trim() || "",
          order: question.order || index + 1,
          options: question.options.map(opt => ({
            text: opt.text?.trim() || `Option ${index + 1}`,
            isCorrect: opt.isCorrect === "true"
          }))
        }))
      };

      if (isCreateMode) {
        await createAssessment(submitData);
        toast.success("Assessment created successfully!", {
          autoClose: 1500,
          onClose: () => navigate(`/proAdmin-dashboard/courseDetails/${courseId}`)
        });

      } else {
        submitData.assessmentId = parseInt(assessmentId);
        await updateAssessment(submitData);
        toast.success("Assessment updated successfully!", {
          autoClose: 1500,
          onClose: () => navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}`)
        });
      }

    } catch (error) {
      toast.error(error.message || "Submission failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}`);
  };

  const switchToEditMode = () => {
    if (isCoursePublished) {
      toast.error("Cannot edit a published course");
      return;
    }
    navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}/edit-assessment?assessmentId=${assessmentId}`);
  };

  const switchToPreviewMode = () => {
    navigate(`/proAdmin-dashboard/courses/${courseId}/modules/${moduleId}/edit-assessment?assessmentId=${assessmentId}&mode=preview`);
  };

  if (pageLoading || courseLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isCreateMode && !assessmentData) {
    return (
      <Alert
        message="Assessment Not Found"
        description="The requested assessment could not be found."
        type="error"
        showIcon
        style={{ margin: "20px" }}
        action={
          <Button size="small" onClick={handleBack}>
            Go Back
          </Button>
        }
      />
    );
  }

  const getHeaderTitle = () => {
    if (isCreateMode) return "Create New Assessment";
    return isPreviewMode ? "Assessment Preview" : "Edit Assessment";
  };

  const getHeaderIcon = () => {
    if (isCreateMode) return <PlusOutlined />;
    return isPreviewMode ? <EyeOutlined /> : <EditOutlined />;
  };

  const getSubmitButtonText = () => {
    return isCreateMode ? "Create Assessment" : "Update Assessment";
  };

  return (
    <Card
      className="card-professional"
      title={
        <Space direction="vertical" size="small">
          <Title level={2} style={{ margin: 0, color: 'white' }}>
            {getHeaderIcon()} {getHeaderTitle()}
          </Title>
          <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
            Course ID: {courseId} | Module ID: {moduleId}
            {assessmentId && ` | Assessment ID: ${assessmentId}`}
            {course && (
              <Tag
                color={course.isPublished ? "green" : "orange"}
                icon={course.isPublished ? <EyeOutlined /> : <EditOutlined />}
                style={{ marginLeft: 8 }}
              >
                {course.isPublished ? "Published" : "Draft"}
              </Tag>
            )}
          </Text>
        </Space>
      }
      extra={
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
        >
          Back to Modules
        </Button>
      }
    >

      {/* Course Published Alert */}
      {isCoursePublished && (
        <Alert
          message="Course Published"
          description="This course is published and cannot be edited. You can only preview the assessment."
          type="warning"
          showIcon
          style={{ marginBottom: "10px" }}
        />
      )}

      {/* Mode Indicator */}
      {isPreviewMode && (
        <Alert
          message="Preview Mode"
          description="You are viewing how students will see this assessment. No changes can be made in this view."
          type="info"
          showIcon
          action={
            !isCoursePublished && (
              <Button size="small" type="primary" onClick={switchToEditMode}>
                Edit Assessment
              </Button>
            )
          }
          style={{ marginBottom: "10px" }}
        />
      )}

      {isCreateMode && (
        <Alert
          message="Create New Assessment"
          description="You are creating a new assessment for this module. Fill in the details below."
          type="info"
          showIcon
          style={{ marginBottom: "10px" }}
        />
      )}

      {/* Instructions - Only show in edit/create mode */}
      {!isPreviewMode && !isCoursePublished && (
        <Alert
          message="Assessment Instructions"
          description={
            <Space direction="vertical" size="small">
              <Text>• Each question must have at least 3 options</Text>
              <Text>• Each question must have at least 1 correct answer</Text>
              <Text>• You can select maximum 2 correct answers per question</Text>
              <Text>• Maximum 5 options allowed per question</Text>
              <Text>• All questions and options must have text</Text>
            </Space>
          }
          type="info"
          showIcon
          icon={<QuestionCircleOutlined />}
          style={{ marginBottom: "10px" }}
        />
      )}

      {/* Inline validation alert (shows validation errors) */}
      {validationAlert && (
        <Alert
          message="Validation Error"
          description={validationAlert}
          type="error"
          showIcon
          closable
          onClose={() => setValidationAlert(null)}
          style={{ marginBottom: 12 }}
        />
      )}

      {/* Assessment Form Container */}
      <div
        style={{
          background: '#FAF8F3',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          border: '1px solid #f0f0f0',
        }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text strong style={{ fontSize: '16px' }}>
            {isCreateMode ? 'Create New Assessment' : (isPreviewMode ? 'Assessment Details' : 'Edit Assessment Details')}
          </Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleAssessmentSubmit}
          requiredMark="optional"
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="title"
                label="Assessment Title"
                rules={[
                  { required: true, message: "Please enter assessment title" },
                  { min: 3, message: "Title must be at least 3 characters" }
                ]}
              >
                <Input
                  placeholder="Enter assessment title"
                  size="large"
                  disabled={isPreviewMode || isCoursePublished}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider>
            <Space>
              <Text strong>Questions</Text>
              <Text type="secondary">({questions.length} questions)</Text>
            </Space>
          </Divider>

          {/* Questions List */}
          {questions.map((question, questionIndex) => (
            <div
              key={question.key}
              style={{
                marginBottom: 16,
                padding: '16px',
                border: '1px solid #e8e8e8',
                borderRadius: '6px',
                background: isPreviewMode || isCoursePublished ? '#fafafa' : 'white'
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px'

              }}>
                <Space>
                  <Text strong style={{ fontSize: '14px' }}>Question {questionIndex + 1}</Text>
                  {!isPreviewMode && !isCoursePublished && questions.length > 1 && (
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeQuestion(questionIndex)}
                      size="small"
                    >
                      Remove Question
                    </Button>
                  )}
                </Space>

                {!isPreviewMode && !isCoursePublished && (
                  <Space>
                    <Text type="secondary" style={{ fontSize: '12px' }}>Order:</Text>
                    <InputNumber
                      value={question.order}
                      onChange={(value) => updateQuestion(questionIndex, 'order', value)}
                      min={1}
                      max={questions.length}
                      size="small"
                      style={{ width: '80px' }}
                    />
                  </Space>
                )}
              </div>

              <Space direction="vertical" style={{ width: "100%" }} size="middle">
                {isPreviewMode || isCoursePublished ? (
                  <div>
                    <Text strong>Question: </Text>
                    <Text>{question.questionText}</Text>
                  </div>
                ) : (
                  <TextArea
                    placeholder="Enter question text..."
                    value={question.questionText}
                    onChange={(e) => updateQuestion(questionIndex, 'questionText', e.target.value)}
                    rows={3}
                    showCount
                    maxLength={500}
                    style={{ background: '#FAF8F3' }}
                  />
                )}



                {question.options.map((option, optionIndex) => (
                  <div
                    key={option.key}
                    style={{
                      padding: '12px',
                      border: option.isCorrect === "true" ? '2px solid #968f07ff' : '1px solid #d9d9d9',
                      borderRadius: '6px',
                      backgroundColor: option.isCorrect === "true" ? '#f5ecb5ff' : 'white',
                      marginBottom: '8px'
                    }}
                  >
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      {isPreviewMode || isCoursePublished ? (
                        <>
                          <Text>{option.text}</Text>
                          {option.isCorrect === "true" && (
                            <Tag color="green" style={{ marginLeft: 8 }}>
                              Correct Answer
                            </Tag>
                          )}
                        </>
                      ) : (
                        <>
                          <input
                            type="checkbox"
                            checked={option.isCorrect === "true"}
                            onChange={() => handleCorrectAnswerChange(questionIndex, optionIndex)}
                            style={{ marginTop: '8px' }}
                          />
                          <Input
                            placeholder={`Option ${optionIndex + 1}`}
                            value={option.text}
                            onChange={(e) => updateOption(questionIndex, optionIndex, 'text', e.target.value)}
                            style={{ flex: 1, background: '#FAF8F3' }}
                            size="large"
                          />
                          {question.options.length > 3 && (
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => removeOption(questionIndex, optionIndex)}
                              style={{ marginTop: '4px' }}
                            />
                          )}
                        </>
                      )}
                    </div>
                    {!isPreviewMode && !isCoursePublished && option.isCorrect === "true" && (
                      <Text type="success" style={{ fontSize: '12px', marginLeft: '28px', display: 'block', marginTop: '4px' }}>
                        ✓ This option is marked as correct
                      </Text>
                    )}
                  </div>
                ))}

                {!isPreviewMode && !isCoursePublished && question.options.length < 5 && (
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => addOption(questionIndex)}
                    block
                  >
                    Add Option ({question.options.length}/5)
                  </Button>
                )}
              </Space>
            </div>
          ))}

          {!isPreviewMode && !isCoursePublished && (
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addQuestion}
              block
              size="large"
              style={{ marginBottom: 24 }}
            >
              Add New Question
            </Button>
          )}

          {!isPreviewMode && !isCoursePublished && (
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  icon={<SaveOutlined />}
                >
                  {getSubmitButtonText()}
                </Button>
                <Button
                  onClick={handleBack}
                  size="large"
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          )}
        </Form>
      </div>
    </Card>
  );
};

export default EditAssessmentPage;